<template>
  <section>
    <div class="section-heading">
      <h2>Контакты</h2>
      <Badge variant="outline">{{ localContacts ? localContacts.length : 0 }}</Badge>
    </div>
    <div v-if="localContacts?.length" class="space-y-3">
      <Card
        v-for="contact in localContacts"
        :key="contact.id"
        :ref="`contact-${contact.id}`"
        :class="['contact-card', { 'highlighted-contact': isHighlighted(contact.id) }]"
        :data-contact-id="contact.id"
      >
        <CardHeader class="contact-card-header">
          <div class="flex items-start gap-2 min-w-0">
            <UserRound :size="16" class="shrink-0 text-primary mt-0.5" />
            <span class="contact-name">{{ contact.name }}</span>
          </div>
          <Badge v-if="isHighlighted(contact.id)" variant="success" class="calling-chip">
            Звонит
          </Badge>
          <Button
            variant="ghost"
            size="icon"
            class="ml-auto shrink-0"
            aria-label="Скопировать ссылку на анкету"
            title="Скопировать ссылку на анкету"
            @click="copySurveyUrl(contact)"
          >
            <Copy :size="15" />
          </Button>
        </CardHeader>
        <CardContent>
          <div class="contact-info">
            <div v-if="contact.position" class="info-row">
              <BriefcaseBusiness :size="14" class="info-icon" />
              <span class="info-value">{{ contact.position }}</span>
            </div>
            <div v-if="contact.phone" class="info-row">
              <Phone :size="14" class="info-icon" />
              <a :href="`tel:${contact.phone}`" class="contact-link">{{ contact.phone }}</a>
            </div>
            <div v-if="contact.email" class="info-row">
              <Mail :size="14" class="info-icon" />
              <a :href="`mailto:${contact.email}`" class="contact-link">{{ contact.email }}</a>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
    <div
      v-if="snackbar.show"
      class="staff-toast"
      :class="{ 'staff-toast--error': snackbar.color === 'error' }"
      role="status"
    >
      {{ snackbar.message }}
      <Button variant="ghost" size="sm" @click="snackbar.show = false">Закрыть</Button>
    </div>
  </section>
</template>

<script setup lang="ts">
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { UserRound, Copy, BriefcaseBusiness, Phone, Mail } from '@lucide/vue';

import { ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';

interface Props {
  contacts: PropertyContact[];
  highlightedContactId?: number | null;
}

const props = withDefaults(defineProps<Props>(), {
  highlightedContactId: null,
});

const localContacts = ref<PropertyContact[]>([]);
const localHighlightedContactId = ref<number | null>(null);

const snackbar = ref({
  show: false,
  message: '',
  color: 'success',
});

// Watch contacts
let notificationTimer: ReturnType<typeof setTimeout> | null = null;
watch(
  () => snackbar.value,
  (notification) => {
    if (notificationTimer) clearTimeout(notificationTimer);
    if (notification.show)
      notificationTimer = setTimeout(() => {
        snackbar.value.show = false;
      }, 3000);
  }
);
onBeforeUnmount(() => {
  if (notificationTimer) clearTimeout(notificationTimer);
});

// Watch contacts
watch(
  () => props.contacts,
  (newVal) => {
    if (newVal) {
      localContacts.value = JSON.parse(JSON.stringify(newVal));
    } else {
      localContacts.value = [];
    }
  },
  { immediate: true, deep: true }
);

// Watch highlightedContactId
watch(
  () => props.highlightedContactId,
  (newVal) => {
    localHighlightedContactId.value = newVal;
    scrollToHighlightedContact();
  },
  { immediate: true }
);

function scrollToHighlightedContact() {
  if (!localHighlightedContactId.value) {
    return;
  }

  nextTick(() => {
    // In Vue 3 with script setup, refs work differently
    // We need to use template refs directly
    const el = document.querySelector(`[data-contact-id="${localHighlightedContactId.value}"]`);

    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      console.warn(`Contact with id ${localHighlightedContactId.value} not found`);
    }
  });
}

async function copySurveyUrl(contact: PropertyContact) {
  const fullUrl = window.location.origin + contact.survey_url;

  try {
    await navigator.clipboard.writeText(fullUrl);
    snackbar.value = {
      show: true,
      message: `Ссылка для ${contact.name} успешно скопирована`,
      color: 'success',
    };
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    const errorMessage = error instanceof Error ? error.message : 'Неизвестная ошибка';
    snackbar.value = {
      show: true,
      message: `Ошибка при копировании ссылки: ${errorMessage}`,
      color: 'error',
    };
  }
}

function isHighlighted(contactId: number): boolean {
  return localHighlightedContactId.value !== null && localHighlightedContactId.value === contactId;
}

onMounted(() => {
  scrollToHighlightedContact();
});
</script>
